import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MessageCircle, Mail, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';

const CTA = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { t } = useLanguage();

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height
    });
  };

  const partners = ['We Advise', 'CIBA Congress', 'COSMOS 3D', 'MODE Modular'];

  return (
    <section 
      className="py-24 bg-gradient-to-br from-red-600 to-red-700 relative overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} />
      </div>

      <div 
        className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl transition-all duration-1000 ease-out"
        style={{
          left: `${mousePosition.x * 100}%`,
          top: `${mousePosition.y * 100}%`,
          transform: 'translate(-50%, -50%)'
        }}
      />
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />

      <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-ping" />
      <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white rounded-full animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-1.5 h-1.5 bg-white rounded-full animate-bounce" />

      <div className="max-w-5xl mx-auto px-6 text-center relative">
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
          <Sparkles className="text-white animate-pulse" size={16} />
          <span className="text-white text-sm font-medium">{t('cta.badge')}</span>
        </div>

        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in">
          {t('cta.title')}
        </h2>
        <p className="text-white/80 text-xl max-w-2xl mx-auto mb-12">
          {t('cta.subtitle')}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <Button 
            size="lg" 
            className="bg-white text-red-600 hover:bg-neutral-100 px-8 py-6 text-lg rounded-full group shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105" 
            asChild
          >
            <Link to="/contact">
              <Calendar className="mr-2 group-hover:rotate-12 transition-transform" size={20} />
              {t('cta.scheduleCall')}
              <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" size={18} />
            </Link>
          </Button>
          <Button 
            size="lg" 
            variant="outline" 
            className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-6 text-lg rounded-full group transition-all duration-300 hover:scale-105" 
            asChild
          >
            <a href="https://wa.me/5491112345678" target="_blank" rel="noopener noreferrer">
              <MessageCircle className="mr-2 group-hover:scale-110 transition-transform" size={20} />
              {t('cta.whatsapp')}
            </a>
          </Button>
        </div>

        <div className="flex items-center justify-center gap-6 text-white/80">
          <a href="mailto:modular@wedevelop.one" className="flex items-center gap-2 hover:text-white transition-colors group">
            <Mail size={18} className="group-hover:scale-110 transition-transform" />
            modular@wedevelop.one
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-6 mt-12 pt-8 border-t border-white/20">
          <div className="text-white/60 text-sm">{t('cta.supportedBy')}</div>
          {partners.map((partner, index) => (
            <span key={index} className="text-white/80 font-medium">{partner}</span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CTA;
